import { makeAutoObservable, runInAction } from 'mobx'
import { AgentActionType, type IAgentActionTagPayload } from 'entities/AgentActions'
import {
  ChatbotDisqualifyBehaviorStatus,
  ChatbotField,
  ChatbotInstructionType,
  type IChatbotFullData,
} from 'entities/Chatbot'
import { CustomFieldsStore } from 'entities/CustomField'
import { ContactsApi } from 'entities/Contacts'
import { TagsApi } from 'entities/Tags'
import { Tag } from 'entities/Tags/model/Tag'
import { Segment } from 'entities/Segment/model/Segment'
import { fetchSendFromInfo, type ISendFromInfo } from 'entities/SendFrom'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { KnowledgeBaseApi } from 'entities/KnowledgeBase'
import { EnrollmentFilters } from 'widgets/ContactEnrollment'
import { MessageFieldStore, EnumVariantMessageField } from 'widgets/MessageField'

const FIELDS_LIMIT = 100

export class DetailsStore {
  private _fields!: ChatbotField[]
  private _fieldsStore = new CustomFieldsStore(FIELDS_LIMIT)

  private _futureSendFrom!: Promise<ISendFromInfo | null>
  private _futureContactsCount!: Promise<number | null>
  private _futureActionsTags = new Map<string, Promise<Tag[]>>()
  private _futureEnrollmentFilters: Promise<EnrollmentFilters> | null = null
  private _futureKnowledgeBaseDocumentNames: Promise<string[]> | null = null
  private _instructionMessageFieldStore: MessageFieldStore | null = null

  constructor(private _data: IChatbotFullData, private _entities: IEntitiesStore) {
    const isUnified = _data.type === ChatbotInstructionType.Unified

    this._initFields()
    this._initSendFrom()
    this._initContactsCount()

    if (isUnified) {
      this._initEnrollmentFilters()
      this._initKnowledgeBaseDocumentNames()
      this._initInstructionMessageFieldStore()
    } else {
      this._initActionTags()
    }

    makeAutoObservable(this)
  }

  get data() {
    return this._data
  }

  get fields() {
    return this._fields
  }

  get dataCollectionInstruction() {
    return this._data.settings?.data_collection_instruction?.value || ''
  }

  get qualifyInstruction() {
    return this._data.settings?.qualify_instruction?.value || ''
  }

  get qualifyEnabled() {
    return this._data.settings?.qualify_enabled?.value ?? false
  }

  get qualifiedActions() {
    return this._data.qualified_actions
  }

  get disqualifiedActions() {
    return this._data.disqualified_actions
  }

  get disqualifiedBehavior() {
    if (this._data.settings?.disqualify_behavior?.value === ChatbotDisqualifyBehaviorStatus.Stop) {
      return 'Politely conclude conversation'
    }

    if (
      this._data.settings?.disqualify_behavior?.value === ChatbotDisqualifyBehaviorStatus.Continue
    ) {
      return 'Continue to success or fallback actions'
    }

    return undefined
  }

  get futureSendFrom() {
    return this._futureSendFrom
  }

  get futureFutureContactsCount() {
    return this._futureContactsCount
  }

  get futureEnrollmentFilters() {
    return this._futureEnrollmentFilters
  }

  get futureKnowledgeBaseDocumentNames() {
    return this._futureKnowledgeBaseDocumentNames
  }

  get instructionMessageFieldStore() {
    return this._instructionMessageFieldStore
  }

  getFutureActionsTags = (ids: number[]) => {
    const key = ids.join(',')
    return this._futureActionsTags.get(key) ?? Promise.resolve([])
  }

  getFieldName = (fieldKey: string) => {
    return this._fieldsStore.fields.find(({ key }) => key === fieldKey)?.name
  }

  loadData = () => Promise.all([this._fieldsStore.loadData()])

  private _initFields = () => {
    this._fields = this._data.fields?.map(ChatbotField.Create) ?? []
    if (!this._fields.length) this._fields = [ChatbotField.Create()]
  }

  private _initSendFrom = () => {
    this._futureSendFrom = fetchSendFromInfo(this._data.send_from, this._entities)
  }

  private _initEnrollmentFilters = () => {
    const filters = this._data.filters
    const enrollmentFilters = EnrollmentFilters.Empty()

    if (!filters) {
      this._futureEnrollmentFilters = Promise.resolve(enrollmentFilters)
      return
    }

    const tagsIds = filters.tags ?? []
    const contactsIds = filters.contacts ?? []
    const segmentsIds = filters.segments ?? []

    this._futureEnrollmentFilters = Promise.all([
      TagsApi.getTagsById(tagsIds).then((tags) => tags.map((tag) => new Tag(tag))),
      this._entities.contactsStore.getByIds(contactsIds),
    ]).then(([tags, contacts]) => {
      const segments = segmentsIds.reduce<Segment[]>((acc, id) => {
        const item = this._entities.segmentsStore.getItem(id)
        if (item) acc.push(item)
        return acc
      }, [])

      const advanced = filters.advanced?.map(
        (f) => new Segment({ id: -1, organization_id: 0, name: '', filters: f, is_favorite: false })
      )

      runInAction(() => {
        enrollmentFilters.sync({ tags, segments, contacts, filters: advanced })
      })

      return enrollmentFilters
    })
  }

  private _initContactsCount = () => {
    if (!this._data.filters) return (this._futureContactsCount = Promise.resolve(null))

    const filtersSize = Object.values(this._data.filters)
      .map((filter) => filter.length ?? 0)
      .reduce((prev, next) => prev + next, 0)
    if (!filtersSize) return (this._futureContactsCount = Promise.resolve(null))

    this._futureContactsCount = ContactsApi.getContactsFiltersCount({
      filtersListGroups: this._data.filters,
    }).then(
      ({ data: { count } }) => count,
      () => null
    )
  }

  private _initInstructionMessageFieldStore = () => {
    this._instructionMessageFieldStore = new MessageFieldStore(
      {
        noCounter: true,
        variant: EnumVariantMessageField.Clean,
        disableMergeFieldsFallback: true,
        resetOnUnmount: false,
      },
      this._entities
    )

    this._instructionMessageFieldStore.setMessageData({
      message: this._data.settings?.instructions?.value ?? '',
      attachments: [],
      isFocus: false,
      isReplace: true,
      isReset: false,
      replaceActionChips: true,
    })
  }

  private _initKnowledgeBaseDocumentNames = async () => {
    const ids = this._data.knowledge_base_documents
    if (!ids?.length) {
      this._futureKnowledgeBaseDocumentNames = Promise.resolve([])
      return
    }
    this._futureKnowledgeBaseDocumentNames = (async () => {
      try {
        const { data } = await KnowledgeBaseApi.getKnowledgeBaseDocuments({ limit: 25, page: 1 })
        return data.data.filter((doc) => ids.includes(doc.id)).map((doc) => doc.name)
      } catch {
        return []
      }
    })()
  }

  private _initActionTags = () => {
    const allActions = [
      ...(this._data.success_actions ?? []),
      ...(this._data.fallback_actions ?? []),
      ...(this._data.qualified_actions ?? []),
      ...(this._data.disqualified_actions ?? []),
    ]

    allActions
      .filter(
        (action) =>
          action.type === AgentActionType.AddTag || action.type === AgentActionType.RemoveTag
      )
      .map((action) => action.settings as IAgentActionTagPayload)
      .forEach((settings) => {
        const ids = settings.tag_ids
        const key = ids.join(',')
        const futureTags = TagsApi.getTagsById(ids).then(
          (tags) => tags.map((tag) => new Tag(tag)),
          () => []
        )

        this._futureActionsTags.set(key, futureTags)
      })
  }
}
